import { useInviteActions } from "@/components/InvitationDialogStacks/hooks/useInviteActions";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrganization } from "@/queries/organizations";
import { WorkspaceInvite } from "@/types/workspace-invite";

const InviteCard = ({
  invite,
  sameOrgInvites,
}: {
  invite: WorkspaceInvite;
  sameOrgInvites: WorkspaceInvite[];
}) => {
  const organization = useOrganization(invite.workspace.organization_id);

  const userDisplayName = invite.inviter.name ?? invite.inviter.email;

  const {
    handleAccept,
    handleDecline,
    isBulkAccepting,
    isBulkDeclining,
    isAccepting,
    isDeclining,
    acceptAll,
    declineAll,
  } = useInviteActions(sameOrgInvites);

  const isDisabled =
    isAccepting || isDeclining || isBulkAccepting || isBulkDeclining;

  return (
    <Dialog defaultOpen={true}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{invite.workspace.name}</DialogTitle>
          <DialogDescription>{organization.data?.name}</DialogDescription>
        </DialogHeader>

        <p className="mt-4 text-sm">
          <strong>{userDisplayName}</strong> invited you to join this workspace
        </p>

        {sameOrgInvites.length > 1 && (
          <div className="mt-4 space-y-2 rounded-lg bg-gray-50 p-4">
            <p className="text-sm font-semibold">
              {sameOrgInvites.length} invites from {organization.data?.name}
            </p>
            <p className="text-xs text-gray-500">
              Save time by accepting all at once
            </p>
            <div className="mt-2 flex h-7 w-full gap-2">
              <Button
                isLoading={isBulkAccepting}
                disabled={isDisabled}
                onClick={acceptAll}
                className="w-full"
              >
                Accept all {sameOrgInvites.length}
              </Button>
              <Button
                isLoading={isBulkDeclining}
                disabled={isDisabled}
                variant="secondary"
                onClick={declineAll}
                className="w-full"
              >
                Decline all
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="justify-end">
          <Button
            isLoading={isDeclining}
            disabled={isDisabled}
            variant="secondary"
            onClick={() => handleDecline(invite.id)}
          >
            Decline
          </Button>
          <Button
            isLoading={isAccepting}
            disabled={isDisabled}
            onClick={() => handleAccept(invite.id)}
          >
            Accept
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteCard;
